import { createSlice } from '@reduxjs/toolkit';
import { uid } from 'uid';
import { isBarChart } from 'utils/panel.util';

const panel = {
    panelType: null,
    panelSubType: null,
    panelId: null,
    config: {
        title: 'New Panel',
        description: '',
        i: null,
        layouts: {},
    },
    content: {},
    data: {
        units: [],
        tags: [],
        values: null,
    },
};

const layout = {
    x: 0,
    y: 0,
    w: 12,
    h: 8,
    minW: 1,
    minH: 0.8,
    maxH: 30,
    rowHeight: 5,
};

const layouts = {
    lg: layout,
    md: layout,
    sm: layout,
    xs: layout,
    xxs: { ...layout, w: 2, h: 6 },
};

const initialState = {
    activePanelIndex: 0,
    activePanelId: 0,
    panels: [],
    currentBreakpoint: 'lg',
};

const getPanels = state => {
    const panels = [...state.panels];
    const activePanel = { ...panels[state.activePanelIndex] };
    return { panels, activePanel };
};

const getNewPanel = () => {
    return {
        ...panel,
        panelId: uid(),
        config: {
            ...panel.config,
            i: uid(),
            layouts,
        },
    };
};

export const panelsSlice = createSlice({
    name: 'panelSlice',
    initialState,
    reducers: {
        setPanels: (state, action) => {
            return {
                ...initialState,
                panels: [...action.payload],
            };
        },
        addNewPanel: (state, action) => {
            return {
                ...initialState,
                panels: [
                    {
                        ...getNewPanel(),
                        panelType: action.payload.panelType,
                        panelSubType: action.payload.panelSubType,
                    },
                    ...state.panels,
                ],
            };
        },
        updatePanelType: (state, action) => {
            const { panels, activePanel } = getPanels(state);

            if (activePanel.panelType !== action.payload.panelType) {
                activePanel.panelType = action.payload.panelType;
                activePanel.data = panel.data;
            }
            activePanel.panelSubType = action.payload.panelSubType;
            if (!isBarChart(activePanel.panelType)) {
                activePanel.content = panel.content;
            }
            panels[state.activePanelIndex] = activePanel;
            return {
                ...state,
                panels,
            };
        },
        updatePanelConfig: (state, action) => {
            const { panels, activePanel } = getPanels(state);
            panels[state.activePanelIndex] = {
                ...activePanel,
                config: { ...action.payload },
            };
            return {
                ...state,
                panels,
            };
        },
        updatePanelContent: (state, action) => {
            const { panels, activePanel } = getPanels(state);
            panels[state.activePanelIndex] = {
                ...activePanel,
                content: action.payload,
            };
            return {
                ...state,
                panels,
            };
        },
        updatePanelData: (state, action) => {
            const { panels, activePanel } = getPanels(state);

            if (Object.keys(activePanel).length) {
                panels[state.activePanelIndex] = {
                    ...activePanel,
                    data: { ...activePanel.data, ...action.payload },
                };
                return {
                    ...state,
                    panels,
                };
            }
            return state;
        },
        resetPanelData: (state, action) => {
            const { panels, activePanel } = getPanels(state);
            panels[state.activePanelIndex] = {
                ...activePanel,
                data: { ...panel.data },
            };
            return {
                ...state,
                panels,
            };
        },
        setActivePanel: (state, action) => {
            return {
                ...state,
            };
        },
        discardPanel: state => {
            return initialState;
        },
    },
});

export const getPanelByIndex = (state, index) => {
    return state.panelsReducer.panels[index];
};

export const getActivePanelIndex = state => {
    return state.panelsReducer.activePanelIndex;
};

export const getPanelType = (state, index) => {
    const panel = state.panelsReducer.panels[index];
    return { panelType: panel?.panelType, panelSubType: panel?.panelSubType };
};

export const getActivePanel = state => {
    const reducer = state.panelsReducer;
    return reducer.panels[reducer.activePanelIndex];
};

export const {
    setPanels,
    addNewPanel,
    updatePanelType,
    updatePanelConfig,
    discardPanel,
    updatePanelContent,
    updatePanelData,
    resetPanelData,
} = panelsSlice.actions;
export default panelsSlice.reducer;
